<div class="py-6 px-[2vw]">
  <div class="px-3 md:px-4 md:py-0 sm:py-0 max-w-[900px] mx-auto">
    <!-- Header -->
    <header class="mb-6 flex items-start gap-3">
      @if (isSmallScreen()) {
        <button
          mat-icon-button
          class="w-10 h-10 rounded-xl bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-500 hover:bg-gray-50 transition-colors shrink-0 mt-0.5"
          (click)="sidenavService.toggle()"
          [matTooltip]="(sidenavService.opened() ? 'Global.Close' : 'Global.Open') | translate"
        >
          <mat-icon class="text-[20px]! w-5! h-5!">{{ sidenavService.opened() ? 'left_panel_close' : 'left_panel_open' }}</mat-icon>
        </button>
      }
      <div class="flex-1 min-w-0">
        <!-- Title row: back button inline with h1 -->
        <div class="flex items-center gap-1 flex-wrap mb-1">
          @if (viewState() === 'USERS') {
            <button mat-icon-button (click)="onBackToGroups()" class="text-gray-400 hover:text-gray-700 -ml-2 transition-colors shrink-0">
              <mat-icon>arrow_back</mat-icon>
            </button>
          }
          <h1 class="text-2xl font-bold m-0">
            @switch (viewState()) {
              @case ('GROUPS') {
                {{ 'GroupOverview.Title.Groups' | translate }}
              }
              @case ('USERS') {
                {{ 'GroupOverview.Title.UsersIn' | translate }}
                <span class="text-[#5a73c5]">"{{ selectedGroup()?.actualName }}"</span>
              }
            }
          </h1>
        </div>
        <p class="text-sm text-gray-500 m-0">{{ 'GroupOverview.Subtitle' | translate }}</p>
      </div>
    </header>

    <!-- Main Card -->
    <div class="bg-white rounded-2xl border border-gray-200 mb-8 overflow-hidden">
      <!-- Toolbar -->
      <div class="p-3 border-b border-gray-100 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <mat-form-field appearance="fill" subscriptSizing="dynamic" class="flex-1 soft-input">
          <mat-icon matPrefix class="text-gray-400 mr-2">search</mat-icon>
          <input
            matInput
            #filterInput
            type="text"
            [value]="searchTerm()"
            (input)="onSearchChange(filterInput.value)"
            autocomplete="off"
            [placeholder]="(viewState() === 'GROUPS' ? 'GroupOverview.SearchGroup' : 'GroupOverview.SearchUser') | translate"
          />
        </mat-form-field>

        @switch (viewState()) {
          @case ('GROUPS') {
            <button mat-flat-button btnType="action" (click)="onCreateGroup()" class="sm:shrink-0">
              <mat-icon>add</mat-icon>
              {{ 'GroupOverview.CreateGroup' | translate }}
            </button>
          }
          @case ('USERS') {
            <button mat-flat-button btnType="action" (click)="onAddMember()" class="sm:shrink-0">
              <mat-icon>person_add</mat-icon>
              {{ 'Group.AddMembers' | translate }}
            </button>
          }
        }
      </div>

      @if (loading()) {
        <mat-progress-bar mode="indeterminate"></mat-progress-bar>
      }

      <!-- Scroll container -->
      <div class="max-h-[calc(100vh-280px)] overflow-y-auto custom-scrollbar">
        @switch (viewState()) {
          <!-- === GROUPS === -->
          @case ('GROUPS') {
            @if (!showTiles()) {
              <!-- Desktop table -->
              <table mat-table [dataSource]="filteredGroups()" class="w-full">
                <ng-container matColumnDef="name">
                  <th mat-header-cell *matHeaderCellDef class="text-left font-semibold py-2 px-4 border-b border-gray-100">
                    {{ 'GroupOverview.Header.Name' | translate }}
                  </th>
                  <td mat-cell *matCellDef="let group" class="py-2 px-4 border-b border-gray-100 text-sm font-medium text-gray-800">
                    {{ group.actualName }}
                  </td>
                </ng-container>

                <ng-container matColumnDef="description">
                  <th mat-header-cell *matHeaderCellDef class="text-left font-semibold py-2 px-4 border-b border-gray-100">
                    {{ 'GroupOverview.Header.Description' | translate }}
                  </th>
                  <td mat-cell *matCellDef="let group" class="py-2 px-4 border-b border-gray-100 text-sm text-gray-500 max-w-[300px] truncate">
                    {{ group.actualDescription | slice: 0 : 100 }}
                    @if (group.actualDescription?.length > 100) {
                      …
                    }
                  </td>
                </ng-container>

                <ng-container matColumnDef="role">
                  <th mat-header-cell *matHeaderCellDef class="text-left font-semibold py-2 px-4 border-b border-gray-100">
                    {{ 'GroupOverview.Header.Role' | translate }}
                  </th>
                  <td mat-cell *matCellDef="let group" class="py-2 px-4 border-b border-gray-100">
                    <span class="role-badge" [class]="'role-' + group.userRole.toLowerCase()">
                      {{ 'GroupOverview.Role.' + group.userRole | translate }}
                    </span>
                  </td>
                </ng-container>

                <ng-container matColumnDef="members">
                  <th mat-header-cell *matHeaderCellDef class="text-left font-semibold py-2 px-4 border-b border-gray-100">
                    {{ 'GroupOverview.Header.Members' | translate }}
                  </th>
                  <td mat-cell *matCellDef="let group" class="py-2 px-4 border-b border-gray-100 text-sm text-gray-600">
                    <div class="flex items-center gap-1.5">
                      <mat-icon class="text-[16px]! h-4! w-4! text-gray-400">group</mat-icon>
                      {{ group.memberCount }}
                    </div>
                  </td>
                </ng-container>

                <ng-container matColumnDef="actions">
                  <th mat-header-cell *matHeaderCellDef class="text-right py-2 px-4 border-b border-gray-100">
                    {{ 'ManageUser.Actions' | translate }}
                  </th>
                  <td mat-cell *matCellDef="let group" class="py-2 px-4 border-b border-gray-100 text-right">
                    <div class="flex items-center justify-end gap-1 action-buttons">
                      <button mat-icon-button btnType="action-icon" (click)="onEditGroup(group, $event)" [matTooltip]="'Global.EditGroup' | translate">
                        <mat-icon>edit</mat-icon>
                      </button>
                      <button
                        mat-icon-button
                        btnType="action-icon"
                        (click)="onDeleteGroup(group, $event)"
                        class="opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-500"
                        [matTooltip]="'Global.Delete' | translate"
                      >
                        <mat-icon>delete_outline</mat-icon>
                      </button>
                    </div>
                  </td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="groupDisplayedColumns; sticky: true" class="bg-gray-50"></tr>
                <tr
                  mat-row
                  *matRowDef="let row; columns: groupDisplayedColumns"
                  (click)="onGroupClicked(row)"
                  class="hover:bg-gray-50/50 transition-colors cursor-pointer group"
                  [title]="'GroupOverview.ClickToView' | translate"
                ></tr>
              </table>
            } @else {
              <!-- Mobile/Tablet tile list for groups -->
              <div class="divide-y divide-gray-100">
                @for (group of filteredGroups(); track group._id) {
                  <div class="flex items-center gap-3 px-4 py-3 hover:bg-gray-50/50 transition-colors cursor-pointer group" (click)="onGroupClicked(group)">
                    <!-- Icon -->
                    <div class="w-9 h-9 rounded-full bg-indigo-50 flex items-center justify-center shrink-0">
                      <mat-icon class="text-[18px]! h-[18px]! w-[18px]! text-indigo-400">group</mat-icon>
                    </div>
                    <!-- Info -->
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-semibold text-gray-800 truncate mb-0">{{ group.actualName }}</p>
                      <p class="text-xs text-gray-400 truncate mb-0">
                        <mat-icon class="text-[12px]! h-3! w-3! align-middle">group</mat-icon>
                        {{ group.memberCount }} ·
                        <span class="role-badge role-badge--sm" [class]="'role-' + group.userRole.toLowerCase()">{{ 'GroupOverview.Role.' + group.userRole | translate }}</span>
                      </p>
                      @if (group.actualDescription) {
                        <p class="text-xs text-gray-400 truncate mb-0">{{ group.actualDescription | slice: 0 : 60 }}</p>
                      }
                    </div>
                    <!-- Actions -->
                    <div class="flex items-center gap-1 shrink-0">
                      <button mat-icon-button btnType="action-icon" (click)="onEditGroup(group, $event)" [matTooltip]="'Global.EditGroup' | translate">
                        <mat-icon>edit</mat-icon>
                      </button>
                      <button
                        mat-icon-button
                        btnType="action-icon"
                        (click)="onDeleteGroup(group, $event)"
                        class="opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-500"
                        [matTooltip]="'Global.Delete' | translate"
                      >
                        <mat-icon>delete_outline</mat-icon>
                      </button>
                    </div>
                  </div>
                }
              </div>
            }

            @if (filteredGroups().length === 0 && !loading()) {
              <div class="flex flex-col items-center justify-center py-16 px-6 text-gray-500">
                <mat-icon class="text-4xl w-12 h-12 mb-3 opacity-50">group_off</mat-icon>
                <p class="text-sm m-0">{{ 'GroupOverview.NoGroupsFound' | translate }}</p>
              </div>
            }
          }

          <!-- === USERS === -->
          @case ('USERS') {
            @if (!showTiles()) {
              <!-- Desktop table -->
              <table mat-table [dataSource]="filteredUsers()" class="w-full">
                <ng-container matColumnDef="photo">
                  <th mat-header-cell *matHeaderCellDef class="text-left font-semibold py-2 px-4 border-b border-gray-100 w-[60px]">
                    {{ 'GroupOverview.UserHeader.Photo' | translate }}
                  </th>
                  <td mat-cell *matCellDef="let user" class="py-2 px-4 border-b border-gray-100 w-[60px]">
                    <img [src]="user.photo" class="w-8 h-8 rounded-full object-cover" alt="User photo" />
                  </td>
                </ng-container>

                <ng-container matColumnDef="name">
                  <th mat-header-cell *matHeaderCellDef class="text-left font-semibold py-2 px-4 border-b border-gray-100">
                    {{ 'GroupOverview.UserHeader.Name' | translate }}
                  </th>
                  <td mat-cell *matCellDef="let user" class="py-2 px-4 border-b border-gray-100 text-sm text-gray-700 capitalize">
                    {{ user.name }}
                  </td>
                </ng-container>

                <ng-container matColumnDef="email">
                  <th mat-header-cell *matHeaderCellDef class="text-left font-semibold py-2 px-4 border-b border-gray-100">
                    {{ 'GroupOverview.UserHeader.Email' | translate }}
                  </th>
                  <td mat-cell *matCellDef="let user" class="py-2 px-4 border-b border-gray-100 text-sm text-gray-700">
                    {{ user.email }}
                  </td>
                </ng-container>

                <ng-container matColumnDef="type">
                  <th mat-header-cell *matHeaderCellDef class="text-left font-semibold py-2 px-4 border-b border-gray-100">
                    {{ 'GroupOverview.UserHeader.Type' | translate }}
                  </th>
                  <td mat-cell *matCellDef="let user" class="py-2 px-4 border-b border-gray-100">
                    <app-role-picker [value]="user.type" [options]="roleOptions" (valueChange)="onRoleChange(user, $event)" />
                  </td>
                </ng-container>

                <ng-container matColumnDef="actions">
                  <th mat-header-cell *matHeaderCellDef class="text-right py-2 px-4 border-b border-gray-100"></th>
                  <td mat-cell *matCellDef="let user" class="py-2 px-4 border-b border-gray-100 text-right">
                    <button
                      mat-icon-button
                      btnType="action-icon"
                      (click)="onDeleteMember(user, $event)"
                      class="opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-500"
                      [matTooltip]="'Global.Delete' | translate"
                    >
                      <mat-icon>delete_outline</mat-icon>
                    </button>
                  </td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="userDisplayedColumns; sticky: true" class="bg-gray-50"></tr>
                <tr mat-row *matRowDef="let row; columns: userDisplayedColumns" class="hover:bg-gray-50/50 transition-colors group"></tr>
              </table>
            } @else {
              <!-- Mobile/Tablet tile list for users -->
              <div class="divide-y divide-gray-100">
                @for (user of filteredUsers(); track user.id) {
                  <div class="flex items-center gap-3 px-4 py-3 hover:bg-gray-50/50 transition-colors group">
                    <img [src]="user.photo" class="w-9 h-9 rounded-full object-cover shrink-0" alt="User photo" />
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-semibold text-gray-800 truncate mb-0 capitalize">{{ user.name }}</p>
                      <p class="text-xs text-gray-400 truncate mb-0">{{ user.email }}</p>
                    </div>
                    <div class="shrink-0">
                      <app-role-picker [value]="user.type" [options]="roleOptions" (valueChange)="onRoleChange(user, $event)" />
                    </div>
                    <button
                      mat-icon-button
                      btnType="action-icon"
                      (click)="onDeleteMember(user, $event)"
                      class="opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-500 shrink-0"
                      [matTooltip]="'Global.Delete' | translate"
                    >
                      <mat-icon>delete_outline</mat-icon>
                    </button>
                  </div>
                }
              </div>
            }

            @if (filteredUsers().length === 0 && !loading()) {
              <div class="flex flex-col items-center justify-center py-16 px-6 text-gray-500">
                <mat-icon class="text-4xl w-12 h-12 mb-3 opacity-50">group_off</mat-icon>
                <p class="text-sm m-0">{{ 'GroupOverview.NoUsersFound' | translate }}</p>
              </div>
            }
          }
        }
      </div>
    </div>
  </div>
</div>
